import React, { CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import './index.less'
import { IDomEditor, IEditorConfig, IToolbarConfig, Boot, DomEditor } from '@wangeditor/editor'
import { Editor, Toolbar } from '@wangeditor/editor-for-react'
import '@wangeditor/editor/dist/css/style.css'
import markdownModule from '@wangeditor/plugin-md'
import { uploadImg } from '../../api/modules/community'
import { validateHtml } from '@/utils/htmlParse'
import ErrorBoundary from '@/library/ErrorBoundary'

Boot.registerModule(markdownModule)

export interface RichEditorProps {
    defaultValue?: string                 // 编辑器默认值  默认空
    readOnly?: boolean                    // 是否只读   默认false
    editorHeight?: number | string        // 编辑器高度 默认高度 300px
    placeholder?: string                  // 默认提示内容
    isBorder?: boolean                    // 是否需要边框
    getHtml?: () => void                  // 获取是否有值
    autoHeight?: {                        // 最小高度和最大高度，仅在只读模式下生效，当该设置生效后，height失效
        min: number,
        max: number
    }
    onChange?: (value: string) => void    // 当输入内容发生变化时调用（内容可能会仅包含空白字符，如需获取实际内容可通过暴露的方法getHtml进行获取）
    simple?: boolean                      // 简洁模式，  默认不是    直接显示的菜单功能比较少
    [key: string]: any
}

let RichEditor: React.FC<RichEditorProps> = (props, ref) => {

    const { defaultValue = '', readOnly = false, editorHeight = 300, placeholder = '请输入内容...', isBorder = true, autoHeight, onChange, simple = false } = props

    // editor 实例
    const [editor, setEditor] = useState<IDomEditor | null>(null)
    const [value, setValue] = useState(defaultValue)

    // 工具栏配置
    const [toolbarConfig, setToolbarConfig] = useState<Partial<IToolbarConfig>>({
        toolbarKeys: [
            "undo",
            "redo",
            "fullScreen",
            "|",
            "headerSelect",
            "bold",
            "underline",
            "italic",
            "color",
            "bgColor",
            // "|",
            // "fontSize",
            // "fontFamily",
            // "lineHeight",
            "|",
            "bulletedList",
            "numberedList",
            // "|",
            {
                key: "group-justify",
                title: "对齐",
                iconSvg: `<svg viewBox="0 0 1024 1024"><path d="M204.8 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M505.6 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M806.4 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path></svg>`,
                menuKeys: [
                    "justifyLeft",
                    "justifyRight",
                    "justifyCenter",
                    "justifyJustify"
                ]
            },
            {
                "key": "group-indent",
                "title": "缩进",
                "iconSvg": "<svg viewBox=\"0 0 1024 1024\"><path d=\"M0 64h1024v128H0z m384 192h640v128H384z m0 192h640v128H384z m0 192h640v128H384zM0 832h1024v128H0z m0-128V320l256 192z\"></path></svg>",
                "menuKeys": [
                    "indent",
                    "delIndent"
                ]
            },
            "insertLink",
            {
                "key": "group-image",
                "title": "图片",
                "iconSvg": "<svg viewBox=\"0 0 1024 1024\"><path d=\"M959.877 128l0.123 0.123v767.775l-0.123 0.122H64.102l-0.122-0.122V128.123l0.122-0.123h895.775zM960 64H64C28.795 64 0 92.795 0 128v768c0 35.205 28.795 64 64 64h896c35.205 0 64-28.795 64-64V128c0-35.205-28.795-64-64-64zM832 288.01c0 53.023-42.988 96.01-96.01 96.01s-96.01-42.987-96.01-96.01S682.967 192 735.99 192 832 234.988 832 288.01zM896 832H128V704l224.01-384 256 320h64l224.01-192z\"></path></svg>",
                "menuKeys": [
                    "insertImage",
                    "uploadImage"
                ]
            },
            "insertTable",
            "codeBlock",
            "divider",
            "|",

        ]
    })

    // 编辑器配置
    const editorConfig: Partial<IEditorConfig> = {
        placeholder: placeholder,
        autoFocus: false,
        MENU_CONF: {
            uploadImage: {
                async customUpload(file: File, insertFn: any) {
                    const formData = new FormData()
                    formData.append('file', file)
                    const res = await uploadImg({ file: formData })
                    if (res?.url) {
                        insertFn(res?.url, res?.url, res?.url)
                    }
                }
            }
        }
    }

    // 退出销毁实例   wangeditor销毁有问题，做一个延迟处理
    useEffect(() => {
        return () => {
            if (editor == null) return
            setTimeout(() => {
                editor?.destroy()
                setEditor(null)
            }, 500);
        }
    }, [])

    // 当前编辑器形态
    useEffect(() => {
        if (readOnly) {
            editor?.disable()
        } else {
            editor?.enable()
        }
    }, [readOnly, editor])

    // 替换字符串中p标签空白字符等
    const replace_p_nbsp_br = useCallback((str: string = '') => {
        return str.replace(/<\/?p>/g, '')
            .replace(/&nbsp;/gi, '')
            .replace(/<br\/?>/g, '')
    }, [])

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])


    // 暴露方法
    useImperativeHandle(ref, () => ({
        getHtml: () => {
            if (editor?.isEmpty()) {
                return ''
            } else {
                const text = editor?.getHtml() ?? ''
                const res = replace_p_nbsp_br(text)
                if (/\S+/.test(res)) {
                    return text
                } else {
                    return ''
                }
            }
        },
        setValue: (val: string = ''): boolean => {
            if (editor) {
                editor?.setHtml(val)
                setValue(val)
                return true
            } else {
                return false
            }
        },
    }))

    const style: CSSProperties = {}
    if (readOnly && autoHeight) {
        style.minHeight = autoHeight.min + 'px'
        style.maxHeight = autoHeight.max + 'px'
        style.overflowY = 'auto'
    } else {
        style.height = typeof editorHeight === 'number' ? editorHeight + 'px' : editorHeight as string
    }

    return <>
        <ErrorBoundary fallback={<div dangerouslySetInnerHTML={{ __html: validateHtml(value) }}></div>}>
            <div style={{ border: isBorder ? '1px solid #E5E5E5' : '', zIndex: 99 }} className='richeditor'>
                {
                    !readOnly && (
                        <Toolbar
                            editor={editor}
                            defaultConfig={toolbarConfig}
                            mode="default"
                            style={{ borderBottom: '1px solid #E5E5E5' }}
                        />
                    )
                }
                <Editor
                    defaultConfig={editorConfig}
                    value={value}
                    onCreated={setEditor}
                    mode="default"
                    onChange={(editor: IDomEditor) => {
                        if (!editor?.isEmpty()) {
                            const html = editor?.getHtml() ?? ''
                            const res = replace_p_nbsp_br(html)
                            if (/\S+/.test(res)) {
                                onChange?.(html)
                                setValue(html)
                            } else {
                                onChange?.('')
                                setValue('')
                            }
                        } else {
                            onChange?.('')
                            setValue('')
                        }
                    }}
                    style={style}
                />
            </div>
        </ErrorBoundary>
    </>
}

RichEditor = forwardRef(RichEditor as any)

export default RichEditor